import { Component, HostListener, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { TableColumnTypeEnum } from 'src/app/shared/components/table/shared/enums';
import { TableConfig, TableFilters } from 'src/app/shared/components/table/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';

import { ExternalAuthorizationsService } from './external-authorizations.service';
import { ExternalAuthorization } from './shared/external-authorization.model';

@Component({
  selector: 'app-external-authorizations',
  templateUrl: './external-authorizations.component.html',
  styleUrls: ['./external-authorizations.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ExternalAuthorizationsComponent implements OnDestroy {
  selectedTabIndex = 0;
  config: TableConfig = {
    displayedColumns: ['name', 'clientId', 'endpoint', 'scope', 'created-by', 'created-at', 'actions'],
    columns: [
      {
        name: 'name',
        translationKey: 'parameters/external-authorizations/name',
        property: 'name',
        type: TableColumnTypeEnum.STRONG,
      },
      { name: 'clientId', translationKey: 'parameters/external-authorizations/client-id', property: 'client_id' },
      { name: 'endpoint', translationKey: 'parameters/external-authorizations/endpoint', property: 'endpoint' },
      { name: 'scope', translationKey: 'parameters/external-authorizations/scope', property: 'scope' },
      {
        name: 'created-by',
        translationKey: 'parameters/external-authorizations/created-by',
        property: 'createdByLabel',
      },
      {
        name: 'created-at',
        translationKey: 'parameters/external-authorizations/created-at',
        property: 'creation_time',
        type: TableColumnTypeEnum.DATE,
      },
    ],
    actions: [
      {
        tooltip: 'see-details',
        clickFn: (element: ExternalAuthorization) => this.seeDetails(element.id),
        icon: 'visibility',
      },
      {
        tooltip: 'delete',
        clickFn: (element: ExternalAuthorization) => this.delete(element.id),
        icon: 'delete',
        color: 'warn',
      },
    ],
  };
  filters: TableFilters = {};

  @HostListener('window:resize')
  onWindowResizeUpdateActionsPosition() {
    this.searchBarService.checkScrollPosition();
  }

  constructor(
    public dialog: MatDialog,
    private externalAuthorizationsService: ExternalAuthorizationsService,
    private searchBarService: SearchBarService,
    private customRouter: CustomRouter,
    private basicService: BasicService,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    topToolbarService.setTitleTranslation('parameters/external-authorizations/title');

    this.searchBarService.sort = { active: 'date', direction: 'desc' };
    this.searchBarService.referer$ = this.externalAuthorizationsService.getExternalAuthorizations;
    this.searchBarService.formatter = externalAuthorizationsService.formatExternalAuthorization;
    this.searchBarService.submitQuery();
  }

  primaryButtonAction = () => {
    this.customRouter.go('auth.alcmeon.fuse.parameters.external-authorizations.create');
  };

  seeDetails = (externalAuthorizationId: number) => {
    const item: ExternalAuthorization = this.searchBarService.itemsBS.value.find(
      ({ id }) => id === externalAuthorizationId
    );
    if (item) {
      const { id } = item;
      this.customRouter.go('auth.alcmeon.fuse.parameters.external-authorizations.details', {
        externalAuthorizationId: id,
      });
    }
  };

  delete = (externalAuthorizationId: number) => {
    const item: ExternalAuthorization = this.searchBarService.itemsBS.value.find(
      ({ id }) => id === externalAuthorizationId
    );
    if (item) {
      const { name } = item;
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: ['medium-size'],
        autoFocus: false,
        data: {
          title: this.basicService.translate(`parameters/external-authorizations/delete-title`),
          description: this.basicService.translate(`parameters/external-authorizations/delete-description`, { name }),
          confirmBtnText: this.basicService.translate('delete'),
          cancelBtnText: this.basicService.translate('close'),
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (item) {
            const { id } = item;
            this.externalAuthorizationsService.deleteExternalAuthorization(id).subscribe({
              next: () => {
                this.searchBarService.submitQuery();
              },
            });
          }
        }
      });
    }
  };

  ngOnDestroy(): void {
    this.searchBarService.onDidLeave();
    this.topToolbarService.clearTitle();
  }
}
